import { useState, useEffect } from 'react';
import { ShoppingBag, Search, Menu, X, User, LogOut } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo-versarc.png';

const Header = () => {
    const { user, logout } = useAuth();
    const { cart } = useCart(); // Assuming cart context provides items count
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Check if we are on home page (for transparency)
    const isHome = location.pathname === '/';

    const headerClass = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${isScrolled || !isHome || isMobileMenuOpen
        ? 'bg-white/90 backdrop-blur-md py-4 shadow-sm text-black'
        : 'bg-transparent py-6 text-white'
        }`;

    const linkClass = `text-xs uppercase tracking-[0.2em] hover:opacity-60 transition-opacity ${isScrolled || !isHome || isMobileMenuOpen ? 'text-black' : 'text-white'
        }`;

    const iconClass = `hover:opacity-60 transition-opacity ${isScrolled || !isHome || isMobileMenuOpen ? 'text-black' : 'text-white'
        }`;

    return (
        <header className={headerClass}>
            <div className="px-6 md:px-12 flex items-center justify-between">
                {/* Logo */}
                <Link to="/" className="z-50 flex items-center">
                    <img src={logo} alt="Versarc" className="h-8 w-auto object-contain" />
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-8">
                    <Link to="/orders" className={linkClass}>My Orders</Link>
                </nav>

                {/* Icons */}
                <div className="hidden md:flex items-center gap-6">
                    <Link to="/search" className={iconClass}>
                        <Search size={20} strokeWidth={1.5} />
                    </Link>
                    <Link to="/settings" className={iconClass}>
                        <User size={20} strokeWidth={1.5} />
                    </Link>
                    <Link to="/cart" className={`${iconClass} relative`}>
                        <ShoppingBag size={20} strokeWidth={1.5} />
                        {cart?.items?.length > 0 && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                        )}
                    </Link>
                    {user && (
                        <button
                            onClick={logout}
                            className={iconClass}
                            title="Sign Out"
                        >
                            <LogOut size={20} strokeWidth={1.5} />
                        </button>
                    )}
                </div>

                {/* Mobile Menu Toggle */}
                <button
                    className="md:hidden z-50"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    {isMobileMenuOpen ? (
                        <X size={24} className="text-black" />
                    ) : (
                        <Menu size={24} className={isScrolled || !isHome ? 'text-black' : 'text-white'} />
                    )}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="absolute top-0 left-0 w-full h-screen bg-white flex flex-col items-center justify-center space-y-8 md:hidden text-black"
                    >
                        <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-2xl font-serif">Home</Link>

                        <Link to="/orders" onClick={() => setIsMobileMenuOpen(false)} className="text-sm uppercase tracking-widest">My Orders</Link>
                        <Link to="/cart" onClick={() => setIsMobileMenuOpen(false)} className="text-sm uppercase tracking-widest">Cart ({cart?.items?.length || 0})</Link>
                        <Link to="/settings" onClick={() => setIsMobileMenuOpen(false)} className="text-sm uppercase tracking-widest">Account</Link>

                        {user && (
                            <button
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    logout();
                                }}
                                className="text-sm uppercase tracking-widest text-red-500 font-medium pt-4"
                            >
                                Sign Out
                            </button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
